export type SubscriptionPlan = 'free' | 'pro';

export type HeaderThemeId =
  | 'purple'
  | 'pink'
  | 'blue'
  | 'green'
  | 'orange'
  | 'dark'
  | 'rainbow'
  | 'gold';

export type HeaderThemeSelection = HeaderThemeId | 'custom';

export interface ImagePosition {
  x: number;
  y: number;
}

export interface HeaderThemeOption {
  id: HeaderThemeId;
  name: string;
  gradient: string;
}

export interface SettingsToggleOption {
  label: string;
  defaultChecked: boolean;
}

export interface UpgradeFeatureGroup {
  title: string;
  items: string[];
}

export interface UpgradeTestimonial {
  quote: string;
  author: string;
}

export const CURRENT_PLAN: SubscriptionPlan = 'free';
export const DEFAULT_HEADER_THEME: HeaderThemeId = 'purple';
export const HEADER_THEME_EVENT_NAME = 'headerThemeChange';
export const DEFAULT_IMAGE_POSITION: ImagePosition = {x: 50, y: 50};
export const DEFAULT_IMAGE_ZOOM = 1;
export const MAX_HEADER_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

export const HEADER_THEME_OPTIONS: HeaderThemeOption[] = [
  {id: 'purple', name: '🟣 Purple Dream', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'},
  {id: 'pink', name: '🌸 Pink Sunset', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'},
  {id: 'blue', name: '🌊 Ocean Blue', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'},
  {id: 'green', name: '🌿 Fresh Green', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'},
  {id: 'orange', name: '🔥 Sunset Orange', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'},
  {id: 'dark', name: '🌙 Dark Night', gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'},
  {id: 'rainbow', name: '🌈 Rainbow', gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'},
  {id: 'gold', name: '✨ Golden Hour', gradient: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'},
];

export const FREE_PLAN_FEATURES = [
  '✅ Unlimited posts',
  '✅ Profile and service marketplace access',
  '✅ 5 GB storage',
  '✅ 100 messages/month',
  '❌ Verified badge',
  '❌ Reel uploads',
  '❌ Go Live broadcasting',
  '❌ HD video (1080p)',
  '❌ Ad-free experience',
];

export const PRO_PLAN_FEATURES = [
  '✅ Verified badge',
  '✅ Unlimited posts & reels',
  '✅ HD video (1080p)',
  '✅ 50 GB storage',
  '✅ Advanced analytics',
  '✅ Ad-free experience',
  '✅ Priority support',
  '✅ Live streaming',
];

export const ACCOUNT_SETTINGS_OPTIONS: SettingsToggleOption[] = [
  {label: 'Email Notifications', defaultChecked: true},
  {label: 'Push Notifications', defaultChecked: true},
  {label: 'Marketing Emails', defaultChecked: false},
];

export const PROFILE_VISIBILITY_OPTIONS = ['Public', 'Private', 'Friends Only'];
export const MESSAGE_PERMISSION_OPTIONS = ['Everyone', 'Followers Only', 'No One'];

export const ABOUT_LINKS = [
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Terms of Service", path: "/terms" },
  { label: "Help Center", path: "/help" },
];

export const UPGRADE_FEATURE_GROUPS: UpgradeFeatureGroup[] = [
  {
    title: '⭐ Enhanced Visibility',
    items: ['Verified badge', 'Featured profile placement', 'Priority in search results', 'Custom profile URL'],
  },
  {
    title: '📸 Unlimited Content',
    items: ['Unlimited posts & reels', 'HD video (1080p, 10 min)', '50 GB storage', '20 highlight reels'],
  },
  {
    title: '📊 Analytics & Insights',
    items: ['Profile views & growth', 'Post performance metrics', 'Audience demographics', 'Optimal posting times'],
  },
  {
    title: '💼 Business Tools',
    items: ['Accept bookings directly', 'Service marketplace', 'Lead generation tools', 'Integrated calendar'],
  },
  {
    title: '🎉 Premium Experience',
    items: ['Ad-free browsing', 'Live streaming (4 hours)', 'Unlimited messages', 'Priority 24/7 support'],
  },
  {
    title: '🎨 Creator Tools',
    items: ['Built-in photo/video editor', 'Post scheduling', 'Content calendar', 'Collaboration features'],
  },
];

export const UPGRADE_TESTIMONIALS: UpgradeTestimonial[] = [
  {
    quote: '"Got 3 new clients in my first month! Pro paid for itself 10x over."',
    author: '- Sarah, Fitness Trainer',
  },
  {
    quote: '"The verified badge gave me instant credibility. Worth every penny."',
    author: '- Mike, Photographer',
  },
  {
    quote: '"Analytics helped me understand my audience. My engagement doubled!"',
    author: '- Emma, Artist',
  },
];
